import React from 'react';
import styled from 'styled-components';
import CTASection from '../components/CTASection';
import Footer from '../components/Footer';
import { useLanguage } from '../i18n/LanguageContext';

const Hero = styled.section`
  position: relative;
  padding: 140px 20px 120px;
  background: radial-gradient(circle at top right, rgba(0, 168, 232, 0.12), transparent 60%),
    linear-gradient(140deg, rgba(247, 248, 252, 1) 0%, rgba(233, 238, 250, 1) 100%);

  @media (max-width: 768px) {
    padding: 110px 18px 90px;
  }

  @media (max-width: 480px) {
    padding: 96px 16px 78px;
  }
`;

const HeroInner = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 40px;
  align-items: center;

  @media (max-width: 768px) {
    gap: 30px;
  }
`;

const Tag = styled.span`
  width: fit-content;
  padding: 6px 16px;
  border-radius: 999px;
  background: rgba(0, 168, 232, 0.16);
  color: var(--primary);
  letter-spacing: 0.32em;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;

  @media (max-width: 768px) {
    align-self: flex-start;
  }
`;

const Title = styled.h1`
  margin: 0;
  font-size: clamp(2.5rem, 4vw, 3.4rem);
  color: var(--secondary);
  line-height: 1.1;

  @media (max-width: 768px) {
    line-height: 1.2;
  }
`;

const Intro = styled.p`
  margin: 0;
  color: var(--text-muted);
  font-size: 1.06rem;
  line-height: 1.8;

  @media (max-width: 768px) {
    font-size: 1.02rem;
  }
`;

const MetricsPanel = styled.div`
  padding: 30px;
  border-radius: var(--radius-lg);
  background: var(--surface);
  border: 1px solid rgba(226, 232, 240, 0.7);
  box-shadow: var(--shadow-soft);
  display: grid;
  gap: 20px;

  @media (max-width: 600px) {
    padding: 26px 24px;
  }
`;

const MetricsTitle = styled.h2`
  margin: 0;
  font-size: 1.6rem;
  color: var(--secondary);
`;

const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 16px;

  @media (max-width: 480px) {
    gap: 14px;
  }
`;

const Metric = styled.div`
  padding: 18px;
  border-radius: var(--radius-md);
  background: rgba(0, 168, 232, 0.08);
  color: var(--secondary);
  font-weight: 600;
  font-size: 0.95rem;

  @media (max-width: 600px) {
    font-size: 0.92rem;
  }
`;

const Section = styled.section`
  padding: 110px 20px;
  background: ${({ $alt }) => ($alt ? '#f4f6fb' : '#ffffff')};

  @media (max-width: 768px) {
    padding: 90px 18px;
  }

  @media (max-width: 480px) {
    padding: 74px 16px;
  }
`;

const SectionInner = styled.div`
  max-width: 1080px;
  margin: 0 auto;
  display: grid;
  gap: 32px;

  @media (max-width: 768px) {
    gap: 26px;
  }
`;

const SectionTitle = styled.h2`
  margin: 0;
  font-size: clamp(2rem, 3vw, 2.8rem);
  color: var(--secondary);
`;

const Paragraph = styled.p`
  margin: 0;
  color: var(--text-muted);
  line-height: 1.75;
`;

const ServiceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 24px;

  @media (max-width: 480px) {
    gap: 18px;
  }
`;

const ServiceCard = styled.article`
  padding: 28px;
  border-radius: var(--radius-lg);
  background: var(--surface);
  border: 1px solid rgba(226, 232, 240, 0.7);
  box-shadow: var(--shadow-card);
  display: flex;
  flex-direction: column;
  gap: 16px;

  @media (max-width: 600px) {
    padding: 24px 22px;
  }
`;

const ServiceTitle = styled.h3`
  margin: 0;
  font-size: 1.3rem;
  color: var(--secondary);
`;

const ServiceText = styled.p`
  margin: 0;
  color: var(--text-muted);
  line-height: 1.7;
  font-size: 0.98rem;
`;

const LogisticsTable = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 24px;

  @media (max-width: 480px) {
    gap: 18px;
  }
`;

const TableColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ColumnTitle = styled.h3`
  margin: 0;
  font-size: 1.1rem;
  color: var(--secondary);
`;

const ColumnList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const ColumnItem = styled.li`
  position: relative;
  padding-left: 18px;
  color: var(--text-muted);
  font-size: 0.95rem;
  line-height: 1.6;

  &:before {
    content: '';
    position: absolute;
    top: 9px;
    left: 0;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--primary);
  }
`;

const RoadTransportPage = () => {
  const { t } = useLanguage();
  const metrics = t('pages.road.metrics');
  const serviceLines = t('pages.road.serviceLines');
  const logisticsData = t('pages.road.logistics');

  return (
    <>
      <Hero>
        <HeroInner>
          <div>
            <Tag>{t('pages.road.tag')}</Tag>
            <Title>{t('pages.road.title')}</Title>
            <Intro>{t('pages.road.intro')}</Intro>
          </div>
          <MetricsPanel>
            <MetricsTitle>{t('pages.road.metricsTitle')}</MetricsTitle>
            <MetricsGrid>
              {metrics.map(item => (
                <Metric key={item}>{item}</Metric>
              ))}
            </MetricsGrid>
          </MetricsPanel>
        </HeroInner>
      </Hero>
      <Section>
        <SectionInner>
          <SectionTitle>{t('pages.road.linesTitle')}</SectionTitle>
          <Paragraph>{t('pages.road.linesIntro')}</Paragraph>
          <ServiceGrid>
            {serviceLines.map(line => (
              <ServiceCard key={line.title}>
                <ServiceTitle>{line.title}</ServiceTitle>
                <ServiceText>{line.text}</ServiceText>
              </ServiceCard>
            ))}
          </ServiceGrid>
        </SectionInner>
      </Section>
      <Section $alt>
        <SectionInner>
          <SectionTitle>{t('pages.road.logisticsTitle')}</SectionTitle>
          <Paragraph>{t('pages.road.logisticsIntro')}</Paragraph>
          <LogisticsTable>
            {logisticsData.map(column => (
              <TableColumn key={column.title}>
                <ColumnTitle>{column.title}</ColumnTitle>
                <ColumnList>
                  {column.items.map(item => (
                    <ColumnItem key={item}>{item}</ColumnItem>
                  ))}
                </ColumnList>
              </TableColumn>
            ))}
          </LogisticsTable>
        </SectionInner>
      </Section>
      <CTASection />
      <Footer />
    </>
  );
};

export default RoadTransportPage;
